import { useMediaDeviceSelect, useRoomContext, useTrackToggle } from '@livekit/components-react';
import { Track } from 'livekit-client';
import {
  Camera,
  CameraOff,
  ChevronUp,
  MessageSquare,
  Mic,
  MicOff,
  MonitorUp,
  MoreVertical,
  PhoneOff,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { DeviceMenu } from '@/components/DeviceMenu';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

/**
 * The floating control bar.
 *
 * Replaces LiveKit's prefab bar so the destructive action can be separated
 * from the routine ones, and so the device pickers hang off the control they
 * belong to rather than living in a settings modal three taps away.
 *
 * Below 640px the bar sheds its secondary controls into an overflow menu.
 * Every control kept on the bar at that width is one that has to be reachable
 * in a hurry — mute, camera, chat, people, leave — and the rest are things you
 * set once. Left un-collapsed the row overflowed a 360px phone.
 */
export function ControlBar({
  peopleOpen,
  peopleCount,
  onTogglePeople,
  chatOpen,
  unreadCount,
  onToggleChat,
  canShare,
  onShare,
  onLeave,
}: {
  peopleOpen: boolean;
  peopleCount: number;
  onTogglePeople: () => void;
  chatOpen: boolean;
  unreadCount: number;
  onToggleChat: () => void;
  /** Only the host holds a link worth sending, so only the host gets the button. */
  canShare: boolean;
  onShare: () => void;
  onLeave: () => void;
}) {
  const room = useRoomContext();
  const narrow = useMediaQuery('(max-width: 860px)');
  const compact = useMediaQuery('(max-width: 640px)');
  const [confirmLeave, setConfirmLeave] = useState(false);

  const mic = useTrackToggle({ source: Track.Source.Microphone });
  const camera = useTrackToggle({ source: Track.Source.Camera });
  const screenShare = useTrackToggle({ source: Track.Source.ScreenShare });

  const microphones = useMediaDeviceSelect({ kind: 'audioinput', room });
  const cameras = useMediaDeviceSelect({ kind: 'videoinput', room });

  // Screen capture needs a window picker, which phones and tablets do not have.
  const canShareScreen = !narrow && typeof navigator.mediaDevices?.getDisplayMedia === 'function';

  return (
    <>
      <div className="lk-control-bar glass pointer-events-auto flex max-w-full items-center gap-1 rounded-2xl border p-1.5 shadow-[var(--shadow-lift)] sm:gap-1.5 sm:p-2">
        <ControlButton
          active={mic.enabled}
          pending={mic.pending}
          OnIcon={Mic}
          OffIcon={MicOff}
          onLabel="Mute"
          offLabel="Unmute"
          onClick={() => void mic.toggle()}
          menu={
            compact ? undefined : (
              <DeviceMenu
                icon={Mic}
                label="Microphone"
                devices={microphones.devices}
                activeDeviceId={microphones.activeDeviceId}
                onSelect={(id) => void microphones.setActiveMediaDevice(id)}
                side="top"
                align="center"
              />
            )
          }
        />

        <ControlButton
          active={camera.enabled}
          pending={camera.pending}
          OnIcon={Camera}
          OffIcon={CameraOff}
          onLabel="Turn camera off"
          offLabel="Turn camera on"
          onClick={() => void camera.toggle()}
          menu={
            compact ? undefined : (
              <DeviceMenu
                icon={Camera}
                label="Camera"
                devices={cameras.devices}
                activeDeviceId={cameras.activeDeviceId}
                onSelect={(id) => void cameras.setActiveMediaDevice(id)}
                side="top"
                align="center"
              />
            )
          }
        />

        {canShareScreen && (
          <ControlButton
            active={screenShare.enabled}
            pending={screenShare.pending}
            OnIcon={MonitorUp}
            OffIcon={MonitorUp}
            onLabel="Stop sharing"
            offLabel="Share your screen"
            onClick={() => void screenShare.toggle()}
            // Sharing is additive: not sharing is the resting state, so it must
            // not wear the red "your mic is off" warning.
            tone="additive"
          />
        )}

        <span className="bg-border mx-0.5 h-8 w-px shrink-0" aria-hidden="true" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-expanded={chatOpen}
              aria-label={chatOpen ? 'Hide chat' : 'Chat'}
              onClick={onToggleChat}
              className={cn('relative', chatOpen && 'bg-white/[0.1]')}
            >
              <MessageSquare className="size-4.5" />
              {unreadCount > 0 && !chatOpen && (
                <span className="bg-primary text-primary-foreground absolute -top-0.5 -right-0.5 grid size-4.5 place-items-center rounded-full text-[10px] font-semibold tabular-nums">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{chatOpen ? 'Hide chat' : 'Chat'}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-expanded={peopleOpen}
              aria-label={peopleOpen ? 'Hide people' : 'People'}
              onClick={onTogglePeople}
              className={cn('relative w-auto min-w-11 px-2.5', peopleOpen && 'bg-white/[0.1]')}
            >
              <Users className="size-4.5" />
              {/* The label stays in the DOM at every width: it is hidden with
                  `display:none`, so the accessible name falls back to
                  aria-label while the text remains a stable test hook. */}
              <span className="hidden text-sm lg:inline">{peopleOpen ? 'Hide people' : 'People'}</span>
              {peopleCount > 0 && (
                <span className="bg-primary text-primary-foreground absolute -top-0.5 -right-0.5 grid size-4.5 place-items-center rounded-full text-[10px] font-semibold tabular-nums lg:static lg:size-5">
                  {peopleCount}
                </span>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{peopleOpen ? 'Hide people' : 'People'}</TooltipContent>
        </Tooltip>

        {/* On a phone the bar is already at its limit, so sharing moves into
            the overflow menu rather than pushing the leave button off screen. */}
        {canShare && !compact && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Share this meeting"
                onClick={onShare}
              >
                <UserPlus className="size-4.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Share this meeting</TooltipContent>
          </Tooltip>
        )}

        {compact && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="More options">
                <MoreVertical className="size-4.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="end" className="min-w-[15rem]">
              {canShare && (
                <>
                  <DropdownMenuItem onSelect={onShare}>
                    <UserPlus />
                    Share this meeting
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}

              <DropdownMenuLabel>Microphone</DropdownMenuLabel>
              {microphones.devices.map((device, index) => (
                <DropdownMenuItem
                  key={device.deviceId}
                  onSelect={() => void microphones.setActiveMediaDevice(device.deviceId)}
                >
                  <span className="truncate">{device.label || `Microphone ${index + 1}`}</span>
                </DropdownMenuItem>
              ))}

              <DropdownMenuSeparator />
              <DropdownMenuLabel>Camera</DropdownMenuLabel>
              {cameras.devices.map((device, index) => (
                <DropdownMenuItem
                  key={device.deviceId}
                  onSelect={() => void cameras.setActiveMediaDevice(device.deviceId)}
                >
                  <span className="truncate">{device.label || `Camera ${index + 1}`}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="destructive"
              size="icon"
              aria-label="Leave the call"
              onClick={() => setConfirmLeave(true)}
              className="ml-0.5 w-13 rounded-xl sm:w-14"
            >
              <PhoneOff className="size-4.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Leave the call</TooltipContent>
        </Tooltip>
      </div>

      {/* Leaving is one tap from muting, and rejoining costs a candidate their
          composure. Worth a confirmation. */}
      <Dialog open={confirmLeave} onOpenChange={setConfirmLeave}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave this interview?</DialogTitle>
            <DialogDescription>
              The call keeps running for everyone else. You can rejoin from the same link.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Stay</Button>
            </DialogClose>
            <Button variant="destructive" onClick={onLeave}>
              <PhoneOff />
              Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * A media control: the toggle itself, plus an optional chevron that opens the
 * device list for that one device.
 *
 * `tone` decides what "off" means. For a mic or camera, off is a state the
 * user needs to notice, so it goes red. For screen share, off is
 * simply the resting state and red would cry wolf.
 */
function ControlButton({
  active,
  pending,
  OnIcon,
  OffIcon,
  onLabel,
  offLabel,
  onClick,
  menu,
  tone = 'media',
}: {
  active: boolean;
  pending?: boolean;
  OnIcon: LucideIcon;
  OffIcon: LucideIcon;
  onLabel: string;
  offLabel: string;
  onClick: () => void;
  menu?: ReactNode;
  tone?: 'media' | 'additive';
}) {
  const Icon = active ? OnIcon : OffIcon;
  const label = active ? onLabel : offLabel;

  return (
    <div className="flex shrink-0 items-center">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={label}
            aria-pressed={tone === 'media' ? !active : active}
            disabled={pending}
            onClick={onClick}
            className={cn(
              menu && 'rounded-r-none pr-1.5',
              tone === 'media' &&
                !active &&
                'bg-destructive text-destructive-foreground hover:bg-destructive/85',
              tone === 'additive' && active && 'bg-primary/25 text-primary-foreground',
            )}
          >
            <Icon className="size-4.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>

      {menu && (
        <div className="relative">
          {/* The DeviceMenu trigger is a full-width row elsewhere; here it is
              collapsed to a chevron tab welded to the toggle. */}
          <div className="[&>button]:min-h-11 [&>button]:w-7 [&>button]:rounded-l-none [&>button]:border-0 [&>button]:bg-white/[0.06] [&>button]:px-0 [&>button>span]:hidden [&>button>svg:first-child]:hidden">
            {menu}
          </div>
          <ChevronUp className="pointer-events-none absolute top-1/2 left-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 opacity-70" />
        </div>
      )}
    </div>
  );
}
